/**
 * DCMA 14-Point — v6 display labels (Sub-piece 4 of Prompt 10D-1).
 *
 * Engine-emitted `p.metric` strings retain their original title-case form
 * for back-compat with existing per-check intelligence and analysis fixtures.
 * The v6 visual standard (PDF + dashboard) re-labels them in sentence case
 * with a few wording tweaks (e.g. "FS Relationships" → "Relationship types").
 *
 * This module is the single source of truth used by both:
 *   - src/pdf/templates/partials/_dcma-table.ejs
 *   - src/dashboard/components/DcmaTable.jsx
 * so the two surfaces never diverge (verified by the coherence integration
 * test).
 */

export const DCMA_METRIC_LABELS_V6 = {
  1:  'Missing predecessors',
  2:  'Missing successors',
  3:  'Relationship types',
  4:  'Leads (negative lag)',
  5:  'Lags',
  6:  'SF/FF relationships',
  7:  'Hard constraints',
  8:  'High float (>44d)',
  9:  'Negative float',
  10: 'High duration (>44d)',
  11: 'Invalid dates',
  12: 'Resource loading',
  13: 'Missed tasks',
  14: 'Critical path test',
};

export const DCMA_THRESHOLD_LABELS_V6 = {
  1:  '<5%',  2:  '<5%',  3:  'FS >90%', 4:  '0',  5:  '<5%',
  6:  'Minimal', 7: '<5%', 8: '<5%',     9:  '0', 10: '<5%',
  11: '0',    12: '100%', 13: '<5%',     14: 'Valid',
};

/**
 * Standard audit note under the 14-point panel. Single source so the PDF
 * partial (_dcma-table.ejs) and the dashboard tab (DcmaTable.jsx) render
 * byte-identical text — the same Rule-12 single-source discipline the label
 * maps above follow. British English; no forward-looking wording.
 */
export const DCMA_PANEL_FOOTNOTE =
  'CPLI and BEI are execution metrics that require a progressed, statused '
  + 'schedule; they are not applicable to pre-status assessments. Logic and '
  + 'relationship-type checks are shown itemised.';

/**
 * Format the result-cell value for a DCMA point in v6 visual form.
 * Pure: same input → same output. Returns a string ready for direct render.
 */
export function getV6ResultDisplay(p) {
  if (!p) return '';
  if (p.status === 'pending') return '\u2014';
  if (p.dcmaNumber === 9) return `${p.result} activities`;
  if (p.dcmaNumber === 14) {
    const cont = String(p.resultDisplay || '').toLowerCase();
    return cont.startsWith('continuous') ? 'Continuous' : 'Discontinuous';
  }
  if (typeof p.result === 'number') {
    const isPercentMetric = String(p.dcmaThreshold || '').includes('%');
    if (isPercentMetric) return `${p.result.toFixed(1)}%`;
    if (Number.isInteger(p.result)) return `${p.result}`;
    return `${p.result.toFixed(1)}`;
  }
  return p.resultDisplay || '';
}

export function getV6MetricLabel(dcmaNumber, fallback = '') {
  return DCMA_METRIC_LABELS_V6[dcmaNumber] || fallback;
}

export function getV6ThresholdLabel(dcmaNumber, fallback = '') {
  return DCMA_THRESHOLD_LABELS_V6[dcmaNumber] || fallback;
}
